import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  message, 
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Divider,
  Input,
  Select,
  DatePicker,
  Tooltip
} from 'antd';
import { 
  DeleteOutlined, 
  EyeOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FilterOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { backendserverurl } from '../../serverconn';
import * as XLSX from 'xlsx';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AgentFacilityVisitsListView = () => {
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [agents, setAgents] = useState([]);
  const [healthCenters, setHealthCenters] = useState([]);
  const [collectionHubs, setCollectionHubs] = useState([]);
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedFacilityType, setSelectedFacilityType] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchVisits();
    fetchAgents();
    fetchHealthCenters();
    fetchCollectionHubs();
  }, []);

  // Apply filters whenever visits or filter criteria change
  useEffect(() => {
    applyFilters();
  }, [visits, searchText, selectedAgent, selectedFacilityType, selectedFacility, dateRange]);

  // Calculate statistics whenever visits data changes
  useEffect(() => {
    calculateStatistics(visits);
  }, [visits]);

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendserverurl}/customqueries/list_facility_visits`, {
        headers: { Authorization: `Token ${token}` }
      });
      setVisits(response.data);
    } catch (error) {
      message.error('Failed to fetch facility visits');
      console.error('Error fetching visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendserverurl}/api/delivery_agents/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchHealthCenters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendserverurl}/api/healthcentres/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setHealthCenters(response.data);
    } catch (error) {
      console.error('Error fetching health centers:', error);
    }
  };

  const fetchCollectionHubs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendserverurl}/api/collectionhubs/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setCollectionHubs(response.data);
    } catch (error) {
      console.error('Error fetching collection hubs:', error);
    }
  };


  const calculateStatistics = (visitsData) => {
    if (!visitsData || visitsData.length === 0) {
      setStatistics({
        total_visits: 0,
        unique_agents: 0,
        health_center_visits: 0,
        collection_hub_visits: 0,
        visits_today: 0,
        visits_this_week: 0
      });
      return;
    }

    // Calculate statistics from visits data
    const totalVisits = visitsData.length;
    
    // Count unique agents
    const uniqueAgents = new Set(visitsData.map(visit => visit.agent)).size;
    
    // Count visits by facility type (handle both old and new formats)
    const healthCenterVisits = visitsData.filter(visit => 
      visit.facility_type === 'Healthcenter' || 
      visit.facility_type === 'health_center' || 
      visit.facility_type === 'Health center'
    ).length;
    
    const collectionHubVisits = visitsData.filter(visit => 
      visit.facility_type === 'Hub' || 
      visit.facility_type === 'collection_hub' ||
      visit.facility_type === 'Collection Hub'
    ).length;

    // Calculate time-based statistics
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const visitsToday = visitsData.filter(visit => visit.visit_date === today).length;
    
    // Calculate start of current week (Monday)
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 1));
    const startOfWeekStr = startOfWeek.toISOString().split('T')[0];
    
    const visitsThisWeek = visitsData.filter(visit => visit.visit_date >= startOfWeekStr).length;

    setStatistics({
      total_visits: totalVisits,
      unique_agents: uniqueAgents,
      health_center_visits: healthCenterVisits,
      collection_hub_visits: collectionHubVisits,
      visits_today: visitsToday,
      visits_this_week: visitsThisWeek
    });
  };

  const fetchStatistics = () => {
    // Calculate statistics from current visits data
    calculateStatistics(visits);
  };

  const applyFilters = () => {
    let filtered = [...visits];

    // Text search
    if (searchText) {
      filtered = filtered.filter(visit =>
        visit.agent_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        visit.health_center_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        visit.collection_hub_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        visit.visit_notes?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Agent filter
    if (selectedAgent) {
      filtered = filtered.filter(visit => visit.agent === selectedAgent);
    }

    // Facility type filter
    if (selectedFacilityType) {
      filtered = filtered.filter(visit => visit.facility_type === selectedFacilityType);
    }

    // Specific facility filter
    if (selectedFacility) {
      if (selectedFacilityType === 'Healthcenter') {
        filtered = filtered.filter(visit => visit.health_center === selectedFacility);
      } else if (selectedFacilityType === 'Hub') {
        filtered = filtered.filter(visit => visit.collection_hub === selectedFacility);
      }
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const startDate = dateRange[0].startOf('day');
      const endDate = dateRange[1].endOf('day');
      filtered = filtered.filter(visit => {
        const visitDate = moment(visit.visit_date);
        return visitDate.isBetween(startDate, endDate, null, '[]');
      });
    }

    setFilteredVisits(filtered);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedAgent(null);
    setSelectedFacilityType(null);
    setSelectedFacility(null);
    setDateRange(null);
  };

  const exportToExcel = () => {
    if (filteredVisits.length === 0) {
      message.warning('No data to export');
      return;
    }

    const exportData = filteredVisits.map(visit => ({
      'Agent Name': visit.agent_name || 'N/A',
      'Agent Code': visit.agent_code || 'N/A',
      'Facility Type': visit.facility_type || 'N/A',
      'Health Center': visit.health_center_name || 'N/A',
      'Collection Hub': visit.collection_hub_name || 'N/A',
      'Address': visit.addres || 'N/A',
      'Latitude': visit.location_latitude || 'N/A',
      'Longitude': visit.location_longitude || 'N/A',
      'Visit Date': visit.visit_date || 'N/A',
      'Visit Time': visit.visit_time || 'N/A',
      'Visit Notes': visit.visit_notes || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Facility Visits');

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Agent Name
      { wch: 15 }, // Agent Code
      { wch: 15 }, // Facility Type
      { wch: 25 }, // Health Center
      { wch: 25 }, // Collection Hub
      { wch: 30 }, // Address
      { wch: 12 }, // Latitude
      { wch: 12 }, // Longitude
      { wch: 12 }, // Visit Date
      { wch: 12 }, // Visit Time
      { wch: 40 }  // Visit Notes
    ];
    ws['!cols'] = colWidths;

    const fileName = `facility_visits_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success('Excel file downloaded successfully');
  };

  const printVisits = () => {
    if (filteredVisits.length === 0) {
      message.warning('No data to print');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Agent Facility Visits Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { color: #1890ff; margin-bottom: 10px; }
            .summary { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Agent Facility Visits Report</h1>
            <p>Generated on: ${moment().format('YYYY-MM-DD HH:mm:ss')}</p>
          </div>
          
          <div class="summary">
            <p><strong>Total Visits:</strong> ${filteredVisits.length}</p>
            <p><strong>Date Range:</strong> ${dateRange ? `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}` : 'All dates'}</p>
          </div>

          <table>
            <thead>
              <tr>
                <th>Agent Name</th>
                <th>Agent Code</th>
                <th>Facility Type</th>
                <th>Facility Name</th>
                <th>Address</th>
                <th>Visit Date</th>
                <th>Visit Time</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${filteredVisits.map(visit => `
                <tr>
                  <td>${visit.agent_name || 'N/A'}</td>
                  <td>${visit.agent_code || 'N/A'}</td>
                  <td>${visit.facility_type || 'N/A'}</td>
                  <td>${visit.health_center_name || visit.collection_hub_name || 'N/A'}</td>
                  <td>${visit.addres || 'N/A'}</td>
                  <td>${visit.visit_date || 'N/A'}</td>
                  <td>${visit.visit_time || 'N/A'}</td>
                  <td>${visit.visit_notes || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>Report generated by Umoja Delivery Management System</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };


  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendserverurl}/api/agent_facility_visits/${id}/`, {
        headers: { Authorization: `Token ${token}` }
      });
      message.success('Visit deleted successfully');
      fetchVisits();
    } catch (error) {
      message.error('Failed to delete visit');
      console.error('Error deleting visit:', error);
    }
  };

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'agent_name',
      key: 'agent_name',
      render: (text, record) => (
        <div>
          <UserOutlined /> {text}
          <br />
          <Tag color="blue">Code: {record.agent_code}</Tag>
        </div>
      ),
    },
    {
      title: 'Facility Type',
      dataIndex: 'facility_type',
      key: 'facility_type',
      render: (type) => {
        const isHealthCenter = type === 'health_center' || type === 'Healthcenter' || type === 'Health center';
        return (
          <Tag color={isHealthCenter ? 'green' : 'orange'}>
            {type || 'Unknown'}
          </Tag>
        );
      },
    },
    {
      title: 'Facility',
      key: 'facility',
      render: (record) => {
        const isHealthCenter = record.facility_type === 'health_center' || 
                              record.facility_type === 'Healthcenter' || 
                              record.facility_type === 'Health center';
        return (
          <div>
            <EnvironmentOutlined /> {
              isHealthCenter 
                ? (record.health_center_name || 'N/A')
                : (record.collection_hub_name || 'N/A')
            }
          </div>
        );
      },
    },
    {
      title: 'Date & Time',
      key: 'datetime',
      render: (record) => (
        <div>
          <CalendarOutlined /> {record.visit_date}
          <br />
          <ClockCircleOutlined /> {record.visit_time}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (record) => (
        <div>
          {record.addres}
          <br />
          <Tag color="geekblue">
            {record.location_latitude}, {record.location_longitude}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'visit_notes',
      key: 'visit_notes',
      ellipsis: true,
      render: (text) => text || 'No notes',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to delete this visit?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Card>
              <Statistic
                title="Total Visits"
                value={statistics.total_visits || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Unique Agents"
                value={statistics.unique_agents || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Health Centers"
                value={statistics.health_center_visits || 0}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Collection Hubs"
                value={statistics.collection_hub_visits || 0}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: '#eb2f96' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Today"
                value={statistics.visits_today || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="This Week"
                value={statistics.visits_this_week || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3}>Agent Facility Visits</Title>
          <Space>
            <Tooltip title="Export to Excel">
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={filteredVisits.length === 0}
              >
                Export Excel
              </Button>
            </Tooltip>
            <Tooltip title="Print Report">
              <Button 
                icon={<PrinterOutlined />}
                onClick={printVisits}
                disabled={filteredVisits.length === 0}
              >
                Print
              </Button>
            </Tooltip>
          </Space>
        </div>

        {/* Search and Filter Controls */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Search by agent, facility, or notes..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Select Agent"
                value={selectedAgent}
                onChange={setSelectedAgent}
                allowClear
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {agents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.FullName} ({agent.agent_code})
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Facility Type"
                value={selectedFacilityType}
                onChange={(value) => {
                  setSelectedFacilityType(value);
                  setSelectedFacility(null); // Reset facility when type changes
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="Healthcenter">Health Center</Option>
                <Option value="Hub">Collection Hub</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Select Facility"
                value={selectedFacility}
                onChange={setSelectedFacility}
                allowClear
                style={{ width: '100%' }}
                disabled={!selectedFacilityType}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {selectedFacilityType === 'Healthcenter' 
                  ? healthCenters.map(center => (
                      <Option key={center.id} value={center.id}>
                        {center.name}
                      </Option>
                    ))
                  : selectedFacilityType === 'Hub'
                  ? collectionHubs.map(hub => (
                      <Option key={hub.id} value={hub.id}>
                        {hub.name}
                      </Option>
                    ))
                  : null
                }
              </Select>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                placeholder={['Start Date', 'End Date']}
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button 
                icon={<FilterOutlined />}
                onClick={handleClearFilters}
                style={{ width: '100%' }}
              >
                Clear Filters
              </Button>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ padding: '8px 0', color: '#666' }}>
                Showing {filteredVisits.length} of {visits.length} visits
              </div>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={filteredVisits}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} visits`,
          }}
        />
      </Card>

    </div>
  );
};

export default AgentFacilityVisitsListView;
